import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import type { RewriteSet, RewriteVariant } from '../../core/types';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { TabPanel, Tabs, TabsProvider } from '../../components/ui/Tabs';
import { useToast } from '../../components/ui/toastContext';

/**
 * Suggested rewrites.
 *
 * The most important thing this component does is make placeholders impossible
 * to miss. The engine never invents a number — where it cannot read a fact off
 * the profile it emits `[metric]` — and the UI has to reinforce that, or someone
 * pastes "Grew signups by [metric]%" onto their live profile. Placeholders are
 * therefore rendered highlighted and monospaced, and there is a standing warning
 * above the text.
 */

/** Splits rewrite text into plain runs and `[bracketed]` placeholder runs. */
function highlightPlaceholders(text: string): ReactNode[] {
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((part, index) => {
    if (!part.startsWith('[') || !part.endsWith(']')) return part;
    return (
      <mark
        key={`${index}-${part}`}
        className="rounded bg-important-100 px-1 font-mono text-[0.9em] text-important-700 dark:bg-important-950 dark:text-important-100"
      >
        {part}
      </mark>
    );
  });
}

function countPlaceholders(text: string): number {
  return (text.match(/\[[^\]]+\]/g) ?? []).length;
}

interface VariantViewProps {
  variant: RewriteVariant;
  field: string;
}

function VariantView({ variant, field }: VariantViewProps) {
  const { copy, copiedId } = useCopyToClipboard();
  const toast = useToast();
  const id = `${field}-${variant.id}`;
  const copied = copiedId === id;
  const gaps = countPlaceholders(variant.text);

  const onCopy = async () => {
    const ok = await copy(variant.text, id);
    if (ok) {
      toast.success(
        gaps > 0
          ? `Copied — remember to replace ${gaps} placeholder${gaps === 1 ? '' : 's'}`
          : 'Copied to your clipboard'
      );
    } else {
      toast.error('Could not copy — select the text and copy it manually');
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">{variant.description}</p>

      <div className="rounded-field border border-line bg-sunken p-3.5">
        <p className="text-sm leading-relaxed whitespace-pre-wrap text-strong">
          {highlightPlaceholders(variant.text)}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={copied ? 'secondary' : 'primary'}
          icon={copied ? <Check /> : <Copy />}
          onClick={onCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <span className="text-xs text-subtle tabular-nums">{variant.text.length} characters</span>
        {gaps > 0 ? (
          <Badge tone="important">
            {gaps} placeholder{gaps === 1 ? '' : 's'} to fill in
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

interface RewriteFieldProps {
  title: string;
  description: string;
  variants: RewriteVariant[];
  field: string;
}

function RewriteField({ title, description, variants, field }: RewriteFieldProps) {
  const [active, setActive] = useState(variants[0]?.id ?? '');
  const current = variants.find((variant) => variant.id === active) ?? variants[0];
  if (!current) return null;

  return (
    <TabsProvider>
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-strong">{title}</h4>
          <p className="mt-0.5 text-xs text-muted">{description}</p>
        </div>

        <Tabs
          label={`${title} rewrite style`}
          items={variants.map((variant) => ({ value: variant.id, label: variant.label }))}
          value={current.id}
          onChange={setActive}
        />

        <TabPanel value={current.id}>
          <VariantView variant={current} field={field} />
        </TabPanel>
      </div>
    </TabsProvider>
  );
}

export function RewritePanel({ rewrites }: { rewrites: RewriteSet }) {
  return (
    <Card>
      <CardBody className="space-y-6">
        <CardHeader
          as="h3"
          title="Suggested rewrites"
          description="Scaffolds built from what you have written — a starting point, not finished copy."
        />

        <p className="rounded-field border border-important-200 bg-important-50 p-3 text-sm text-important-700 dark:border-important-600 dark:bg-important-950 dark:text-important-100">
          <strong className="font-semibold">
            Fill in every highlighted gap before you publish.
          </strong>{' '}
          This tool will never invent a number for you, because a made-up metric on a real profile
          is worse than no metric at all.
        </p>

        <RewriteField
          field="headline"
          title="Headline"
          description="Front-load the first 60 characters — that is roughly all that survives in search results."
          variants={rewrites.headline}
        />

        <hr className="border-line" />

        <RewriteField
          field="about"
          title="About"
          description="The first ~265 characters show before the “…see more” fold. Put your hook there."
          variants={rewrites.about}
        />
      </CardBody>
    </Card>
  );
}
